/**
 * Thin API client. Reads VITE_API_BASE and VITE_API_TOKEN at build-time.
 * For local dev, set them in dashboard/.env.local.
 *
 * In production we recommend putting Cloudflare Access in front of the
 * dashboard host so the bearer token is never exposed.
 */
const BASE = (import.meta.env.VITE_API_BASE || "").replace(/\/+$/, "");
const TOKEN = import.meta.env.VITE_API_TOKEN || "";

function authHeader() {
  return TOKEN ? { Authorization: `Bearer ${TOKEN}` } : {};
}

async function request(path, opts = {}) {
  if (!BASE) throw new Error("API base not configured. Set VITE_API_BASE in dashboard/.env.local");
  const res = await fetch(`${BASE}${path}`, {
    ...opts,
    headers: {
      "content-type": "application/json",
      ...authHeader(),
      ...(opts.headers || {})
    }
  });
  let body = null;
  try { body = await res.json(); } catch { body = null; }
  if (!res.ok) {
    const err = new Error(body?.error || `http_${res.status}`);
    err.status = res.status;
    err.body = body;
    throw err;
  }
  return body;
}

export const api = {
  overview: () => request("/api/overview"),
  myPosts: (limit = 100) => request(`/api/posts/mine?limit=${limit}`),
  competitorPosts: (limit = 100) => request(`/api/posts/competitors?limit=${limit}`),
  postHistory: (urn) => request(`/api/posts/${encodeURIComponent(urn)}/history`),
  followers: (days = 90) => request(`/api/followers?days=${days}`),
  trending: () => request("/api/trending"),
  newsletters: () => request("/api/newsletters"),
  newsletterIssues: (slug) => request(`/api/newsletters/${encodeURIComponent(slug)}/issues`),
  competitors: () => request("/api/competitors"),
  addCompetitor: (data) => request("/api/competitors", { method: "POST", body: JSON.stringify(data) }),
  removeCompetitor: (id) => request(`/api/competitors/${encodeURIComponent(id)}`, { method: "DELETE" }),
  draftPost: (data) => request("/api/ai/draft-post", { method: "POST", body: JSON.stringify(data) }),
  recommendations: () => request("/api/ai/recommendations", { method: "POST" }),
  recommendationsRecent: () => request("/api/ai/recommendations/recent"),
  newsletterIdeas: () => request("/api/ai/newsletter-ideas", { method: "POST" }),
  competitorDigest: () => request("/api/ai/competitor-digest", { method: "POST" })
};

export const config = { hasBase: !!BASE, hasToken: !!TOKEN, base: BASE };
